import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Button,
  ButtonIcon,
  CardBase,
  CardBaseContent,
  CardBaseHeader,
  FlexContainer,
  Label,
  Spinner,
} from '@facephi/ui-react';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useDownloadJson } from '../../hooks';
import {
  TeseoCredentials,
  UserPersonalInformation,
} from '../../state/model/user';
import { getUserDetail } from '../../state/queries/users';
import { CredentialsCard } from '../credentialsCard/CredentialsCard';
import { EmptyData } from '../emptyData';
import { Teseo as TeseoImage } from '../images';
import {
  StyledCredentialsCard,
  StyledCustomTabs,
  StyledHeader,
  StyledListElement,
  StyledQrLabel,
} from './Styles';

export const CredentialsTab = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { downloadJson } = useDownloadJson();

  const [teseoSelected, setTeseoSelected] = useState<TeseoCredentials>();

  const handleSelected = (teseo: TeseoCredentials) => {
    setTeseoSelected(teseo);
  };

  const { data: { getUserDetails } = {}, loading } = useQuery<{
    getUserDetails: UserPersonalInformation;
  }>(getUserDetail, {
    variables: {
      userId: id,
    },
  });

  useEffect(() => {
    getUserDetails?.credentials &&
      setTeseoSelected(getUserDetails?.credentials[0]);
  }, [getUserDetails]);

  return (
    <StyledCustomTabs flexDirection="column" rowGap="1.6" flex="1">
      {/* Header */}
      <StyledHeader
        justifyContent="flex-end"
        forwardedAs="section"
        columnGap="2"
        $paddingSmall
      >
        {teseoSelected?.data && (
          <ButtonIcon
            size="S"
            iconName="DownloadSimple"
            onClick={() => downloadJson(teseoSelected?.data)}
          />
        )}
        <Button size="S">{t('Add new')}</Button>
      </StyledHeader>
      {/* Content */}
      {loading ? (
        <Spinner />
      ) : !getUserDetails?.credentials.length ? (
        <CardBase flex="1">
          <EmptyData />
        </CardBase>
      ) : (
        <FlexContainer columnGap="1.6" flex="1" as="section">
          {/* Credentials */}
          <StyledCredentialsCard>
            <CardBaseHeader>
              <Label semibold>{t('Credentials')}</Label>
            </CardBaseHeader>
            <CardBaseContent flexDirection="column" rowGap="1.6" flex="1">
              {getUserDetails?.credentials?.map((item, index) => (
                <CredentialsCard
                  image={<TeseoImage />}
                  key={index}
                  expirationDate={item.expirationDate}
                  issuanceDate={item.issuanceDate}
                  name={item.credentialType}
                  onClick={() => handleSelected(item)}
                  isSelected={teseoSelected?.credentialId === item.credentialId}
                />
              ))}
            </CardBaseContent>
          </StyledCredentialsCard>
          {/* Details */}
          <CardBase flex="1">
            <CardBaseHeader>
              <Label semibold>{t('Credentials details')}</Label>
            </CardBaseHeader>
            <CardBaseContent flexDirection="column">
              <>
                <FlexContainer as="ul" flexDirection="column" flex="1">
                  {teseoSelected?.data &&
                    Object.entries(teseoSelected.data).map(([key, value]) => (
                      <StyledListElement
                        forwardedAs="li"
                        key={teseoSelected.credentialId + key}
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Label fontSize="12">{t(key)}</Label>
                        <Label semibold textAlign="right">
                          {t(value)}
                        </Label>
                      </StyledListElement>
                    ))}
                </FlexContainer>
                {teseoSelected?.credentialId && (
                  <>
                    <StyledQrLabel semibold>{t('QR Code')}</StyledQrLabel>
                    <QRCodeSVG value={teseoSelected?.credentialId} size={83} />
                  </>
                )}
              </>
            </CardBaseContent>
          </CardBase>
        </FlexContainer>
      )}
    </StyledCustomTabs>
  );
};
